/**
 * The order pages' pieces: the two header pills, the status pill, the
 * purchase order page's tab card, the order's Order step panel (its lines,
 * was the Lines tab) and the Activity timeline (the order's side sheet, the
 * purchase order's tab). Production and Delivery are `ChainPanels`; the
 * order's box is `OrderActionBox`; Details is `DetailsPanels`.
 *
 * - A step panel is the rail's own rows (`RailRow`), each row's detail in
 *   its ⓘ dialog; no tables, no cards inside the order's card.
 * - What the order points at (a purchase order) is a link chip.
 * - Status is the backend's words; its problem colours the rail's amber and
 *   red.
 *
 * Every label and sentence is the backend's (doc 16 §2); this lays them out.
 * Staff only: every caller who can read an order sees our cost and margin
 * (doc 15 §3), and a null cost is not known yet.
 */
import { useState, type ReactNode } from "react";
import { Link } from "react-router";
import type {
  ActivityEntry,
  ActivityParty,
  OrderHealth,
  OrderLineView,
  OrderStatus,
  OrderView,
} from "@shared-core/data/order-to-invoice-types";
import { type StaffArtworkLine } from "@shared-core/data/artwork-staff-types";
import { lineArtworkCell } from "@shared-core/domain/artwork-staff";
import type { OrderRailStep } from "@shared-core/domain/order-lifecycle-rail";
import {
  deliveryDestinationShort,
  versionLabelOf,
} from "@shared-core/domain/order-to-invoice-presentation";
import {
  formatQuotedPrice,
  formatShortDateOrDash,
} from "@shared-core/domain/formatters";
import {
  DELIVERY_NOT_YET_PRICED,
  formatPriceOrNotYetPriced,
} from "@shared-core/domain/checkout-delivery";
import { paths } from "@shared-core/routing/routes";
import { Badge } from "../../primitives/badge";
import { Button } from "../../primitives/button";
import { Card, CardContent } from "../../primitives/card";
import { cn } from "../../primitives/utils";
import { InfoTip } from "../../patterns/InfoTip";
import { RailRow, RailRows } from "../../patterns/LifecycleRail";
import { ObjectChip } from "../../patterns/ObjectChip";
import type { Command } from "@shared-core/domain/object-ui/contract";
import { ChangeDeliveryControl } from "./ChainPanels";
import { LineDatesCard } from "./LineDatesCard";
import {
  DetailItem,
  PanelHeading,
  ViewDetail,
  nodeOfTone,
  useFocusRef,
} from "./StepPanelParts";

const pounds = formatQuotedPrice;

// ── Pills ─────────────────────────────────────────────────────────────────

/** The backend's label; green only for a finished order, everything else neutral. */
export function OrderStatusPill({
  status,
  label,
}: {
  status: OrderStatus;
  label: string;
}) {
  const finished = status === "delivered" || status === "received_into_stock";
  return <Badge variant={finished ? "success" : "secondary"}>{label}</Badge>;
}

const HEALTH_VARIANT: Record<OrderHealth, "warning" | "error"> = {
  at_risk: "warning",
  late: "error",
};

/** The backend's label, coloured by its health; omitted when there is nothing to say. */
export function HealthPill({
  health,
  label,
}: {
  health: OrderHealth | null;
  label: string | null;
}) {
  if (!health || !label) return null;
  return <Badge variant={HEALTH_VARIANT[health]}>{label}</Badge>;
}

/** A row's status: done is green, a problem the rail's amber or red, else neutral. */
export type StatusTone = "neutral" | "at_risk" | "late" | "done";

const TONE_VARIANT: Record<
  StatusTone,
  "secondary" | "warning" | "error" | "success"
> = {
  neutral: "secondary",
  at_risk: "warning",
  late: "error",
  done: "success",
};

export function StatusPill({
  tone,
  children,
}: {
  tone: StatusTone;
  children: ReactNode;
}) {
  return <Badge variant={TONE_VARIANT[tone]}>{children}</Badge>;
}

// ── One container per tab ─────────────────────────────────────────────────

/** The tab's one container, with an optional heading and its ⓘ. */
export function TabCard({
  title,
  info,
  infoLabel,
  summary,
  children,
}: {
  title?: string;
  info?: ReactNode;
  infoLabel?: string;
  /** A one-line summary, right-aligned on the heading's row. */
  summary?: ReactNode;
  children: ReactNode;
}) {
  return (
    <Card>
      <CardContent className="space-y-3 py-4">
        {title && (
          <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-1">
            <div className="flex items-center gap-1">
              <h2 className="text-base font-semibold">{title}</h2>
              {info && (
                <InfoTip label={infoLabel ?? `About ${title.toLowerCase()}`}>
                  {info}
                </InfoTip>
              )}
            </div>
            {summary && (
              <p className="text-sm tabular-nums text-muted-foreground">
                {summary}
              </p>
            )}
          </div>
        )}
        {children}
      </CardContent>
    </Card>
  );
}

// ── Lines ─────────────────────────────────────────────────────────────────

/**
 * Who fulfils a line, as a chip: a bought line's purchase order, always a
 * link; "—" for anything else (a made line's job, a pick, a service's work
 * arrive with their steps).
 */
function FulfilledBy({ line }: { line: OrderLineView }) {
  const p = line.production;
  if (!p) return <span className="text-muted-foreground">—</span>;
  const supplier = p.supplier_name ?? p.supplier_no ?? undefined;
  if (!p.purchase_order_no || !p.purchase_order_self)
    return <ObjectChip label="Not placed yet" detail={supplier} />;
  return (
    <ObjectChip
      label={p.purchase_order_no}
      detail={supplier}
      // The purchase order's own page: `purchase_order_self` names it, the route manifest builds it.
      to={paths.purchaseOrderDetail({ purchaseOrderNo: p.purchase_order_no })}
    />
  );
}

/** What a repeat repeats, as a chip to its page: an order line, or a Tharstern job. */
function RepeatOfChip({
  repeat,
}: {
  repeat: NonNullable<OrderLineView["repeat_of"]>;
}) {
  const to =
    repeat.kind === "order_line" && repeat.order_no
      ? paths.orderDetail({
          orderNo: repeat.order_no,
          step: "order",
          line: repeat.line_no ?? undefined,
        })
      : repeat.kind === "tharstern_job" && repeat.job_no
        ? paths.jobWorkspace({ jobNo: repeat.job_no })
        : undefined;
  return <ObjectChip label={repeat.label} to={to} />;
}

/** A split line's versions: each one's quantity, and where its delivery lines go. */
function VersionsTable({ line }: { line: OrderLineView }) {
  return (
    <table className="w-full max-w-3xl text-sm">
      <thead>
        <tr className="text-left text-xs text-muted-foreground">
          <th className="py-1 pr-6 font-medium">Version</th>
          <th className="py-1 pr-6 text-right font-medium">Qty</th>
          <th className="py-1 font-medium">Delivers to</th>
        </tr>
      </thead>
      <tbody>
        {line.versions.map((v) => (
          <tr key={`${v.label}-${v.name}`}>
            <td className="py-1 pr-6">{versionLabelOf(v)}</td>
            <td className="py-1 pr-6 text-right tabular-nums">
              {v.quantity.toLocaleString("en-GB")}
            </td>
            <td className="py-1">
              {line.delivery_lines
                .filter((d) => d.version_label === v.label)
                .map((d) => deliveryDestinationShort(d.destination))
                .join("; ") || "—"}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** A kitting service's contents: the stock items it packs, and where the kit goes. */
function KitContents({ line }: { line: OrderLineView }) {
  const to = line.delivery_lines[0]?.destination;
  return (
    <div className="space-y-2">
      <table className="w-full max-w-xl text-sm">
        <thead>
          <tr className="text-left text-xs text-muted-foreground">
            <th className="py-1 pr-6 font-medium">Stock item</th>
            <th className="py-1 text-right font-medium">Qty</th>
          </tr>
        </thead>
        <tbody>
          {(line.uses?.stock_items ?? []).map((item) => (
            <tr key={item.stock_item_ref}>
              <td className="py-1 pr-6 tabular-nums">{item.stock_item_ref}</td>
              <td className="py-1 text-right tabular-nums">
                {item.quantity.toLocaleString("en-GB")}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {to && (
        <p className="text-sm">
          <span className="text-muted-foreground">To </span>
          {deliveryDestinationShort(to)}
        </p>
      )}
    </div>
  );
}

/** A step's page address at a line: `?step=production&line=.01`. */
export type StepHref = (step: OrderRailStep, lineNo?: string) => string;

/** One of a line's own steps, for its chain in the line's detail. */
interface LineChainStep {
  step: OrderRailStep;
  name: string;
  status: string;
  tone: StatusTone;
}

/**
 * A line's own chain, from the steps that are built: its artwork, what
 * produces it and where that stands, then its deliveries. Each links to that
 * step at the line. (Release and invoice join as their steps are built.)
 */
function lineChainOf(
  line: OrderLineView,
  artwork: StaffArtworkLine | null,
): LineChainStep[] {
  const steps: LineChainStep[] = [];
  const artworkCell = artwork && lineArtworkCell(artwork);
  if (artworkCell)
    steps.push({ step: "artwork", name: "Artwork", ...artworkCell });
  const m = line.production_milestone;
  if (m) {
    steps.push({
      step: "production",
      name: "Production",
      status: [m.phrase, m.note].filter(Boolean).join(" · ") || "—",
      tone:
        m.state === "done"
          ? "done"
          : m.is_late
            ? "late"
            : m.state === "attention"
              ? "at_risk"
              : "neutral",
    });
  }
  if (line.delivery_lines.length > 0) {
    const done = line.delivery_lines.filter(
      (d) => d.outstanding_quantity === 0,
    ).length;
    steps.push({
      step: "delivery",
      name: "Delivery",
      status: `${done} of ${line.delivery_lines.length} complete`,
      tone: done === line.delivery_lines.length ? "done" : "neutral",
    });
  }
  return steps;
}

function LineChain({
  line,
  artwork,
  stepHref,
}: {
  line: OrderLineView;
  artwork: StaffArtworkLine | null;
  stepHref: StepHref;
}) {
  const steps = lineChainOf(line, artwork);
  if (steps.length === 0) return null;
  return (
    <section aria-label={`The chain of ${line.line_no}`} className="space-y-1">
      <h4 className="text-xs font-medium text-muted-foreground">
        This line's chain
      </h4>
      <ol className="grid gap-2 sm:grid-cols-3">
        {steps.map((s) => (
          <li key={s.step} className="min-w-0 rounded-md border px-2 py-1.5">
            <Link
              to={stepHref(s.step, line.line_no)}
              className="block text-xs text-muted-foreground hover:underline"
            >
              {s.name}
            </Link>
            <span className="block text-sm">
              <StatusPill tone={s.tone}>{s.status}</StatusPill>
            </span>
          </li>
        ))}
      </ol>
    </section>
  );
}

/** Everything a line's ▸ used to open on the Lines tab, and its own chain. */
function LineDetail({
  line,
  artwork,
  stepHref,
}: {
  line: OrderLineView;
  artwork: StaffArtworkLine | null;
  stepHref: StepHref;
}) {
  const p = line.production;
  return (
    <>
      <p className="font-medium">{line.name.full}</p>
      {/* Both, when a line has both: what uses it, and what it uses. */}
      {line.used_in_label && (
        <p className="text-muted-foreground">{line.used_in_label}</p>
      )}
      {line.uses && <p className="text-muted-foreground">{line.uses.label}</p>}
      <dl className="grid grid-cols-2 gap-x-8 gap-y-3 md:grid-cols-4">
        <DetailItem label={line.route_label ? "Route" : "Service"}>
          {line.route_label ?? line.service_label ?? "—"}
        </DetailItem>
        <DetailItem label="Cost">
          {line.cost_pence !== null
            ? pounds(line.cost_pence)
            : (line.cost_label ?? "Not known yet")}
          {/* A stock call-off's cost says what it is; unpriced, the label stands in for the figure. */}
          {line.cost_pence !== null && line.cost_label && (
            <span className="block text-muted-foreground">
              {line.cost_label}
            </span>
          )}
        </DetailItem>
        <DetailItem label="Margin">
          {line.margin_pence !== null ? pounds(line.margin_pence) : "—"}
        </DetailItem>
        <DetailItem label="Fulfilled by">
          <FulfilledBy line={line} />
        </DetailItem>
        {line.lead_days_label && (
          <DetailItem label="Lead time">{line.lead_days_label}</DetailItem>
        )}
        {line.vat_label && (
          <DetailItem label="VAT">{line.vat_label}</DetailItem>
        )}
        {p?.supplier_reference && (
          <DetailItem label="Supplier's reference">
            {p.supplier_reference}
          </DetailItem>
        )}
        {line.name.spec && (
          <DetailItem label="Spec" wide>
            {line.name.spec}
          </DetailItem>
        )}
        {line.options.map((option) => (
          <DetailItem key={option.name} label={option.name} wide>
            {option.value}
          </DetailItem>
        ))}
        <DetailItem label="Priced" wide>
          {line.pricing.label}
        </DetailItem>
        {line.repeat_of && (
          <DetailItem label="Repeat" wide>
            <RepeatOfChip repeat={line.repeat_of} />
          </DetailItem>
        )}
        {line.name.previous_name && (
          <DetailItem label="Was" wide>
            {line.name.previous_name}
          </DetailItem>
        )}
        {p && line.production_milestone?.note && (
          <DetailItem label="Latest" wide>
            {line.production_milestone.note}
          </DetailItem>
        )}
        {line.extra && (
          <>
            <DetailItem label="Added as an extra">
              {formatShortDateOrDash(line.extra.added_at)} ·{" "}
              {line.extra.added_by}
            </DetailItem>
            {line.extra.note && (
              <DetailItem label="Customer agreement" wide>
                {line.extra.note}
              </DetailItem>
            )}
          </>
        )}
      </dl>
      {line.versions.length > 0 && (
        <section
          aria-label={`Versions of ${line.line_no}`}
          className="space-y-1"
        >
          <h4 className="text-xs font-medium text-muted-foreground">
            Versions: the same spec, different artwork; a quantity change keeps
            their split
          </h4>
          <VersionsTable line={line} />
        </section>
      )}
      {line.is_kit && (line.uses?.stock_items.length ?? 0) > 0 && (
        <section aria-label={`Kit ${line.line_no}`} className="space-y-1">
          <h4 className="text-xs font-medium text-muted-foreground">
            Kit contents
          </h4>
          <KitContents line={line} />
        </section>
      )}
      <LineChain line={line} artwork={artwork} stepHref={stepHref} />
    </>
  );
}

/** A row's status tone, as the rail's node: done is done, a problem its dot. */
function nodeOfLine(line: OrderLineView) {
  // A cancelled line has nothing left to do: its status says why.
  if (line.is_cancelled) return nodeOfTone("done");
  // A line with nothing produced or delivered yet has not started.
  const started =
    line.production_milestone !== null &&
    line.production_milestone.state !== "pending";
  return nodeOfTone(line.status.tone, started);
}

/**
 * The Order step's panel (was the Lines tab): one rail row per line — #,
 * what, then how many, the price and where it stands — and its ⓘ for the
 * rest, its own chain included. The order's total (and carriage) is on the
 * heading's row. 3c: a cancelled line is struck through with when and why;
 * an open one offers *Cancel line* (or a 🔒 with orders' reason it can't
 * be) and, with two or more versions, *Name versions*. A part-sent line
 * offers *Cancel the rest* in its place, and reads what was sent and
 * cancelled once it is; an invoiced line offers finance *Credit…* (doc 98).
 */
export function LinesStepPanel({
  order,
  focusLineNo,
  stepHref,
  busy = false,
  onCancelLine,
  onNameVersions,
  onChangeDelivery,
  onRunCommand,
}: {
  order: OrderView;
  focusLineNo: string | null;
  stepHref: StepHref;
  busy?: boolean;
  /** Cancel line (3c): offered where the line carries a `cancel_url`. */
  onCancelLine?: (lineRef: string) => void;
  /** Name versions (3c): offered on a line with two or more versions. */
  onNameVersions?: (lineRef: string) => void;
  /** *Change delivery…* (doc 106 §9.4): offered on an item line carrying `change_delivery`. */
  onChangeDelivery?: (lineRef: string) => void;
  /** Open a supplier-quoted line's stored quote document (V1 completion E3): fetched with the caller's auth. */
  /** Run one of the line's object-contract commands (confirm date, re-promise): the page opens the command shell. */
  onRunCommand?: (command: Command, lineLabel: string) => void;
}) {
  const focusRef = useFocusRef();
  return (
    <div className="space-y-2" data-section-id="order.lines">
      <PanelHeading
        title="Lines"
        summary={
          <>
            {order.delivery_pence !== null &&
              `Delivery ${pounds(order.delivery_pence)} · `}
            {order.has_delivery_not_yet_priced &&
              `${DELIVERY_NOT_YET_PRICED} · `}
            Order total {pounds(order.value_pence)}, ex VAT
          </>
        }
      />
      <RailRows ariaLabel="Lines">
        {order.lines.map((line) => {
          const isFocused = line.line_no === focusLineNo;
          const unnamed = line.versions.filter((v) => !v.name);
          const cancelUrl = line.cancel_url;
          const labelsUrl = line.set_version_labels_url;
          return (
            <RailRow
              key={line.line_ref}
              rowRef={isFocused ? focusRef : undefined}
              isFocused={isFocused}
              {...nodeOfLine(line)}
              label={line.line_no}
              name={
                <>
                  {line.is_cancelled ? (
                    <span className="text-muted-foreground line-through">
                      {line.name.title}
                    </span>
                  ) : (
                    line.name.title
                  )}
                  {line.extra && (
                    <Badge variant="outline" className="ml-2">
                      Extra
                    </Badge>
                  )}
                </>
              }
              spokenName={[
                line.name.title,
                line.extra && "an extra",
                line.is_cancelled && "cancelled",
              ]
                .filter(Boolean)
                .join(", ")}
              status={
                <span className="inline-flex flex-wrap items-center gap-x-1.5 tabular-nums">
                  <span className={cn(line.is_cancelled && "line-through")}>
                    {line.kit
                      ? line.kit.summary
                      : line.quantity.toLocaleString("en-GB")}
                    {line.versions.length > 0 &&
                      ` in ${line.versions.length} versions`}
                  </span>
                  {!line.is_cancelled && unnamed.length > 0 && labelsUrl && (
                    <span className="text-muted-foreground">
                      (
                      {unnamed.length === line.versions.length
                        ? "not named yet"
                        : `${unnamed.map((v) => v.label).join(", ")} not named yet`}
                      )
                    </span>
                  )}
                  <span aria-hidden>·</span>
                  <span className={cn(line.is_cancelled && "line-through")}>
                    {formatPriceOrNotYetPriced(line.price_pence)}
                  </span>
                  <span aria-hidden>·</span>
                  <span>
                    {line.is_cancelled && line.cancelled_label
                      ? line.cancelled_label
                      : (line.rest_cancelled_label ?? line.status.label)}
                  </span>
                  {line.sourcing_label && (
                    <>
                      <span aria-hidden>·</span>
                      <span>{line.sourcing_label}</span>
                    </>
                  )}
                  {line.cancel_refusal && onCancelLine && (
                    <InfoTip
                      icon="lock"
                      label={`Why ${line.line_no} can't be cancelled`}
                    >
                      {line.cancel_refusal}
                    </InfoTip>
                  )}
                </span>
              }
              actions={
                ((labelsUrl && onNameVersions) ||
                  (cancelUrl && onCancelLine) ||
                  (line.change_delivery && onChangeDelivery) ||
                  line.credit) && (
                  <>
                    {line.change_delivery && onChangeDelivery && (
                      <ChangeDeliveryControl
                        line={line}
                        busy={busy}
                        onChangeDelivery={onChangeDelivery}
                      />
                    )}
                    {line.credit && (
                      <Button size="sm" variant="outline" asChild>
                        <Link
                          aria-label={`Credit ${line.line_no} ${line.name.title}`}
                          to={paths.invoiceDetail({
                            invoiceNo: line.credit.invoice_no,
                            raise: true,
                            line: line.credit.invoice_line_id,
                          })}
                        >
                          Credit…
                        </Link>
                      </Button>
                    )}
                    {labelsUrl && onNameVersions && (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={busy}
                        aria-label={`Name versions of ${line.line_no} ${line.name.title}`}
                        onClick={() => onNameVersions(line.line_ref)}
                      >
                        Name versions…
                      </Button>
                    )}
                    {cancelUrl && onCancelLine && (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={busy}
                        aria-label={
                          line.cancel_rest
                            ? `Cancel the rest of ${line.line_no} ${line.name.title}`
                            : `Cancel line ${line.line_no} ${line.name.title}`
                        }
                        onClick={() => onCancelLine(line.line_ref)}
                      >
                        {line.cancel_rest
                          ? line.cancel_rest.label
                          : "Cancel line…"}
                      </Button>
                    )}
                  </>
                )
              }
              detail={
                <ViewDetail title={`Line ${line.line_no} ${line.name.title}`}>
                  <LineDetail
                    line={line}
                    artwork={
                      order.artwork?.lines.find(
                        (a) => a.line_no === line.line_no,
                      ) ?? null
                    }
                    stepHref={stepHref}
                  />
                </ViewDetail>
              }
            />
          );
        })}
      </RailRows>
      {/* N4: each dated item line's own Dates card (mock-up state 7), under the rows. */}
      {order.lines.some((l) => l.dates && !l.is_cancelled) && (
        <div className="space-y-3" data-section-id="order.dates">
          {order.lines.map(
            (line) =>
              line.dates &&
              !line.is_cancelled && (
                <LineDatesCard
                  key={line.line_ref}
                  dates={line.dates}
                  commands={line.commands ?? []}
                  lineLabel={`${line.line_ref} · ${line.name.full}`}
                  busy={busy}
                  onRunCommand={onRunCommand}
                />
              ),
          )}
        </div>
      )}
    </div>
  );
}

// ── Activity ──────────────────────────────────────────────────────────────

/** The same chips, in the same order, on every page: All, then each party with events. */
const PARTY_FILTERS: Array<{ id: ActivityParty | "all"; label: string }> = [
  { id: "all", label: "All" },
  { id: "customer", label: "Customer" },
  { id: "staff", label: "Staff" },
  { id: "supplier", label: "Supplier" },
  { id: "finance", label: "Finance" },
];

/** "29 Sep 11:05": the date and time of an event, on one line. */
function whenOf(at: string): string {
  const d = new Date(at);
  return `${formatShortDateOrDash(at)} ${d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/London" })}`;
}

/**
 * A flat list, newest first, one line per event, filtered by whose step it
 * was; a logged call's note under its line. The order's Activity is this in
 * a side sheet (⋯ → Activity); the purchase order's is `ActivityPanel`.
 */
/** A timeline entry; the purchase order page's also say where each step came from (supplier interface spec 09 §3). */
type TimelineEntry = ActivityEntry & { source_label?: string | null };

export function ActivityTimeline({ entries }: { entries: TimelineEntry[] }) {
  const [party, setParty] = useState<ActivityParty | "all">("all");
  const shown = entries.filter((e) => party === "all" || e.party === party);
  return (
    <div className="space-y-3">
      <div
        role="group"
        aria-label="Show activity from"
        className="flex flex-wrap gap-1.5"
      >
        {PARTY_FILTERS.filter(
          (f) => f.id === "all" || entries.some((e) => e.party === f.id),
        ).map((f) => (
          <button
            key={f.id}
            type="button"
            aria-pressed={party === f.id}
            onClick={() => setParty(f.id)}
            className={cn(
              "rounded-full border px-2.5 py-0.5 text-xs font-medium outline-none focus-visible:ring-2 focus-visible:ring-ring",
              party === f.id
                ? "border-foreground bg-foreground text-background"
                : "border-border text-muted-foreground hover:text-foreground",
            )}
          >
            {f.label}
          </button>
        ))}
      </div>
      {shown.length === 0 ? (
        <p className="text-sm text-muted-foreground">Nothing recorded yet.</p>
      ) : (
        <ol className="space-y-1.5 text-sm">
          {shown.map((e, i) => (
            <li key={`${e.at}-${i}`}>
              <span className="tabular-nums text-muted-foreground">
                {whenOf(e.at)}
              </span>
              <span className="text-muted-foreground"> · </span>
              {e.text}
              {e.source_label && (
                <span className="ml-1.5 rounded border border-border px-1 py-px font-mono text-[10px] uppercase text-muted-foreground">
                  {e.source_label}
                </span>
              )}
              <span className="text-muted-foreground"> · {e.by}</span>
              {e.note && (
                <span className="mt-0.5 block whitespace-pre-line text-muted-foreground">
                  {e.note}
                </span>
              )}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

/** The purchase order's Activity tab: the timeline in the tab's card. */
export function ActivityPanel({ entries }: { entries: TimelineEntry[] }) {
  return (
    <TabCard>
      <ActivityTimeline entries={entries} />
    </TabCard>
  );
}
